import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertTriangle, Flag, Calendar, MapPin } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8; // ~16 seconds

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('polling'); // 'polling' | 'paid' | 'unpaid' | 'expired' | 'error'
  const [payment, setPayment] = useState(null);
  const attemptsRef = useRef(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        if (cancelled) return;
        setPayment(res.data);

        if (res.data.payment_status === 'paid') {
          setState('paid');
          return;
        }

        if (res.data.status === 'expired') {
          setState('expired');
          return;
        }

        attemptsRef.current += 1;
        if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
          setState('unpaid');
          return;
        }
        timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState('error');
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [sessionId]);

  return (
    <div data-testid="thank-you-page" className="min-h-screen bg-[#0A0A0A]">
      <Navigation />

      <section className="pt-32 pb-16 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {state === 'polling' && (
            <div className="bg-[#141414] border border-[#2A2A2A] p-10 text-center" data-testid="thank-you-polling">
              <Loader2 className="w-12 h-12 text-[#E8651A] animate-spin mx-auto mb-6" />
              <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] tracking-wider mb-2">
                CONFIRMING YOUR PAYMENT...
              </h1>
              <p className="text-[#A19D98]">Hold tight — we're checking with Stripe.</p>
            </div>
          )}

          {state === 'paid' && (
            <div className="bg-[#141414] border border-[#E8651A]/40 p-10 text-center" data-testid="thank-you-paid">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#E8651A]/10 flex items-center justify-center"
              >
                <CheckCircle2 className="w-12 h-12 text-[#E8651A]" />
              </motion.div>

              <h1 className="font-['Oswald'] text-4xl sm:text-5xl text-[#F2EDE6] tracking-wider mb-3">
                YOU'RE IN.
              </h1>
              <p className="text-[#E8651A] font-['Oswald'] tracking-wider text-sm mb-6">
                PAYMENT CONFIRMED · SEE YOU ON THE ROAD
              </p>

              <p className="text-[#A19D98] leading-relaxed mb-8 max-w-md mx-auto">
                Thank you for joining <span className="text-[#F2EDE6]">{payment?.rally_name || 'the rally'}</span>.
                We'll email you in the coming days with the final details — meet-up point, hotel info, and route.
              </p>

              {/* Payment summary */}
              <div className="bg-[#0A0A0A] border border-[#2A2A2A] p-5 text-left space-y-2 mb-8">
                {payment?.rally_name && (
                  <div className="flex items-center gap-3 text-sm">
                    <Flag className="w-4 h-4 text-[#E8651A] flex-shrink-0" />
                    <span className="text-[#F2EDE6]">{payment.rally_name}</span>
                  </div>
                )}
                {payment?.amount_total != null && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-[#A19D98] w-4">$</span>
                    <span className="text-[#F2EDE6]">
                      ${(payment.amount_total / 100).toFixed(2)} {payment.currency?.toUpperCase()}
                    </span>
                  </div>
                )}
                {payment?.customer_email && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-[#A19D98] w-4">@</span>
                    <span className="text-[#F2EDE6]">{payment.customer_email}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3 justify-center">
                <Link
                  to="/account/payments"
                  data-testid="thank-you-view-receipt"
                  className="btn-primary"
                >
                  View My Payments
                </Link>
                <Link
                  to="/rallies"
                  data-testid="thank-you-browse-rallies"
                  className="btn-secondary"
                >
                  Browse More Rallies
                </Link>
              </div>

              <p className="text-[#A19D98] text-xs mt-8">
                Questions? Email{' '}
                <a href="mailto:contact@revlinerally.com" className="text-[#E8651A] hover:underline">
                  contact@revlinerally.com
                </a>
              </p>
            </div>
          )}

          {state === 'unpaid' && (
            <div className="bg-[#141414] border border-[#2A2A2A] p-10 text-center" data-testid="thank-you-unpaid">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-6" />
              <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] tracking-wider mb-3">
                WE'RE STILL CONFIRMING
              </h1>
              <p className="text-[#A19D98] mb-6">
                Your payment may still be processing. Check your email for a receipt, or visit your account to see the latest status.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/account/payments" className="btn-primary">
                  My Payments
                </Link>
                <Link to="/rallies" className="btn-secondary">
                  Back to Rallies
                </Link>
              </div>
            </div>
          )}

          {state === 'expired' && (
            <div className="bg-[#141414] border border-[#2A2A2A] p-10 text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-6" />
              <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] tracking-wider mb-3">
                SESSION EXPIRED
              </h1>
              <p className="text-[#A19D98] mb-6">This checkout session is no longer valid.</p>
              <Link to="/rallies" className="btn-primary">Back to Rallies</Link>
            </div>
          )}

          {state === 'error' && (
            <div className="bg-[#141414] border border-[#2A2A2A] p-10 text-center">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-6" />
              <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] tracking-wider mb-3">
                SOMETHING WENT WRONG
              </h1>
              <p className="text-[#A19D98] mb-6">
                We couldn't confirm your payment right now. If you were charged, email{' '}
                <a href="mailto:contact@revlinerally.com" className="text-[#E8651A] hover:underline">
                  contact@revlinerally.com
                </a>{' '}
                and we'll sort it out.
              </p>
              <Link to="/rallies" className="btn-secondary">Back to Rallies</Link>
            </div>
          )}
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default ThankYou;
